// Single source of truth for all plans. Keep in sync with Stripe products.
export type PlanCategory = "creator" | "agency";

export type PlanLimits = {
  /** HeyGen AI avatar videos per month. 0 = no access. */
  heygenVideos: number;
  /** Luma AI cinematic clips per month. 0 = no access. */
  lumaVideos: number;
  /** Number of client accounts allowed (limits are pooled across them for agency plans). */
  clientAccounts: number;
  /** Number of invitable team members (does NOT include the account owner). */
  teamMembers: number;
};

export type Plan = {
  priceId: string;
  productId: string;
  name: string;
  price: number; // monthly USD (current/founding price)
  originalPrice?: number; // crossed-out "was" price
  foundingOffer?: boolean; // show "First 100 members" badge
  category: PlanCategory;
  popular?: boolean;
  /** Short tagline e.g. "1 account" / "Up to 10 client accounts" */
  accountLine: string;
  /** Platforms summary e.g. "Instagram & Facebook" */
  platforms: string;
  /** Categories of features included on this plan (matches the FeaturesExplained panels). */
  categories: string[];
  /** Bullet meta lines rendered with emoji prefixes. */
  meta: string[];
  /** Detailed feature list (kept for billing pages, not shown on landing). */
  features: string[];
  limits: PlanLimits;
};

const ALL_CATEGORIES = [
  "AD CREATION",
  "CAMPAIGN MANAGEMENT",
  "AUDIENCE TARGETING",
  "CAMPAIGN GOALS",
  "ANALYTICS",
];

const FULL_CATEGORIES = [
  ...ALL_CATEGORIES,
  "CONTENT",
  "COMPETITOR RESEARCH",
  "VIDEO & VOICE",
];

export const PLANS: Plan[] = [
  {
    priceId: "starter_monthly",
    productId: "starter",
    name: "Launch",
    price: 149,
    originalPrice: 297,
    foundingOffer: true,
    category: "creator",
    accountLine: "1 account",
    platforms: "Instagram & Facebook",
    categories: ALL_CATEGORIES,
    meta: [
      "Up to 15 campaigns",
      "14-day free trial — cancel anytime",
      "Email support",
    ],
    features: [
      "1 account across Instagram & Facebook",
      "Ad creation, campaign management, audience targeting, campaign goals, analytics",
      "Up to 15 campaigns",
      "14-day free trial — cancel anytime",
      "Email support",
    ],
    limits: { heygenVideos: 0, lumaVideos: 0, clientAccounts: 1, teamMembers: 0 },
  },
  {
    priceId: "growth_monthly",
    productId: "growth",
    name: "Scale",
    price: 297,
    originalPrice: 597,
    foundingOffer: true,
    category: "creator",
    popular: true,
    accountLine: "1 account",
    platforms: "Instagram, Facebook, TikTok, Google Search + Display",
    categories: ALL_CATEGORIES,
    meta: [
      "Unlimited campaigns",
      "14-day free trial — cancel anytime",
      "Priority email support",
    ],
    features: [
      "1 account across Instagram, Facebook, TikTok & Google Search & Display",
      "Ad creation, campaign management, audience targeting, campaign goals, analytics",
      "Unlimited campaigns",
      "14-day free trial — cancel anytime",
      "Priority email support",
    ],
    limits: { heygenVideos: 0, lumaVideos: 0, clientAccounts: 1, teamMembers: 0 },
  },
  {
    priceId: "growth_ai_monthly",
    productId: "growth_ai",
    name: "Dominate",
    price: 497,
    originalPrice: 797,
    foundingOffer: true,
    category: "creator",
    accountLine: "1 account",
    platforms: "Instagram, Facebook, TikTok, Google Search + Display & YouTube",
    categories: FULL_CATEGORIES,
    meta: [
      "Unlimited campaigns",
      "30 AI videos included every month",
      "Early access to all new features",
      "1 team member",
      "14-day free trial — cancel anytime",
      "Priority chat support",
    ],
    features: [
      "1 account across Instagram, Facebook, TikTok, Google Search & Display & YouTube",
      "Everything in Growth plus Content, Competitor Research, and Video & Voice",
      "Unlimited campaigns",
      "30 AI videos included every month",
      "Early access to all new features",
      "1 team member",
      "14-day free trial — cancel anytime",
      "Priority chat support",
    ],
    limits: { heygenVideos: 30, lumaVideos: 0, clientAccounts: 1, teamMembers: 1 },
  },
  {
    priceId: "agency_starter_monthly",
    productId: "agency_starter",
    name: "Agency Launch",
    price: 1997,
    category: "agency",
    accountLine: "Up to 10 client accounts",
    platforms: "Instagram & Facebook",
    categories: ALL_CATEGORIES,
    meta: [
      "Up to 15 campaigns per client",
      "Up to 2 team members",
      "14-day free trial — 1 client included, cancel anytime",
      "Dedicated support",
    ],
    features: [
      "Up to 10 client accounts",
      "Instagram & Facebook for every client",
      "Ad creation, campaign management, audience targeting, campaign goals, analytics",
      "Up to 15 campaigns per client",
      "Up to 2 team members",
      "14-day free trial — 1 client included, cancel anytime",
      "Dedicated support",
    ],
    limits: { heygenVideos: 0, lumaVideos: 0, clientAccounts: 10, teamMembers: 2 },
  },
  {
    priceId: "agency_growth_monthly",
    productId: "agency_growth",
    name: "Agency Scale",
    price: 2997,
    category: "agency",
    popular: true,
    accountLine: "Up to 10 client accounts",
    platforms: "Instagram, Facebook, TikTok, Google Search + Display",
    categories: ALL_CATEGORIES,
    meta: [
      "Unlimited campaigns per client",
      "Up to 3 team members",
      "14-day free trial — 1 client included, cancel anytime",
      "Priority dedicated support",
    ],
    features: [
      "Up to 10 client accounts",
      "Instagram, Facebook, TikTok & Google for every client",
      "Ad creation, campaign management, audience targeting, campaign goals, analytics",
      "Unlimited campaigns per client",
      "Up to 3 team members",
      "14-day free trial — 1 client included, cancel anytime",
      "Priority dedicated support",
    ],
    limits: { heygenVideos: 0, lumaVideos: 0, clientAccounts: 10, teamMembers: 3 },
  },
  {
    priceId: "agency_pro_video_monthly",
    productId: "agency_pro_video",
    name: "Agency Dominate",
    price: 4997,
    category: "agency",
    accountLine: "Up to 10 client accounts",
    platforms: "Instagram, Facebook, TikTok, Google Search + Display & YouTube",
    categories: FULL_CATEGORIES,
    meta: [
      "Unlimited campaigns per client",
      "30 AI videos per month per client",
      "Up to 5 team members",
      "Early access to all new features",
      "14-day free trial — 1 client included, cancel anytime",
      "Priority dedicated support",
    ],
    features: [
      "Up to 10 client accounts",
      "Instagram, Facebook, TikTok & Google for every client",
      "Everything in Agency Growth plus Content, Competitor Research, and Video & Voice",
      "Unlimited campaigns per client",
      "30 AI videos per month per client",
      "Up to 5 team members",
      "Early access to all new features",
      "14-day free trial — 1 client included, cancel anytime",
      "Priority dedicated support",
    ],
    limits: { heygenVideos: 30, lumaVideos: 0, clientAccounts: 10, teamMembers: 5 },
  },
];

export function findPlan(priceId: string | null | undefined): Plan | undefined {
  if (!priceId) return undefined;
  return PLANS.find((p) => p.priceId === priceId || p.productId === priceId);
}
